import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, ArrowRight, XCircle } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", data: null, error: null });

  useEffect(() => {
    if (!sid) { setStatus({ state: "error", error: "Missing session_id" }); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      if (attempts > 15) { setStatus({ state: "timeout" }); return; }
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        setStatus({ state: "error", error: formatApiError(err) });
      }
    };
    poll();
  }, [sid]);

  return (
    <div className="min-h-[80vh] bg-cream flex items-center pt-32 pb-20" data-testid="page-payment-success">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {status.state === "polling" && (
          <>
            <div className="animate-pulse text-primary text-lg mb-4">Confirming your payment…</div>
            <p className="text-warmgrey">Please wait a moment while we finalise your order.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle2 size={64} style={{ color: "#C9A961" }} className="mx-auto mb-6" />
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Thank you.</h1>
            <p className="text-warmgrey text-lg mb-8">
              Your payment for <span className="font-medium">{status.data?.product_name}</span> — AUD ${status.data?.amount?.toLocaleString()} — has been received.
            </p>
            {status.data?.voucher_code && (
              <div className="bg-white p-6 rounded-xl border border-border-warm mb-8 inline-block">
                <p className="eyebrow mb-2">Voucher Code</p>
                <p className="font-serif text-3xl mb-4" style={{ color: "#C9A961" }}>{status.data.voucher_code}</p>
                <a
                  href={`${API_BASE}/vouchers/${status.data.voucher_code}/certificate`}
                  className="btn-primary"
                  target="_blank" rel="noreferrer"
                  data-testid="download-certificate"
                >
                  <Download size={16} /> Download Certificate
                </a>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Link to="/" className="btn-outline">Return Home</Link>
              <Link to="/contact" className="btn-primary">Speak with us <ArrowRight size={16} /></Link>
            </div>
          </>
        )}
        {(status.state === "expired" || status.state === "timeout" || status.state === "error") && (
          <>
            <XCircle size={56} className="mx-auto mb-6 text-warmgrey" />
            <h1 className="font-serif text-3xl mb-4">Something went wrong.</h1>
            <p className="text-warmgrey mb-6">{status.error || "Your session has expired or could not be verified. Please try again."}</p>
            <Link to="/legacy-films#packages" className="btn-primary">Back to Packages</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[80vh] bg-cream flex items-center pt-32 pb-20" data-testid="page-payment-cancel">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <XCircle size={56} className="mx-auto mb-6 text-warmgrey" />
        <h1 className="font-serif text-4xl mb-4">Payment cancelled.</h1>
        <p className="text-warmgrey mb-8">No charge was made. You can return whenever you're ready.</p>
        <Link to="/legacy-films#packages" className="btn-primary">Back to Packages</Link>
      </div>
    </div>
  );
}
